import type { Money, MoneyDTO } from '../types.js';
import { currencyExponent, parseMoney } from '../money.js';
import { formatMoney } from '../policy/templates.js';
import { dateMentions, monthAbbreviation, shortDate, type DateMention } from './dates.js';
import { approximateMentions } from './approximate.js';

export interface AmountMention {
  index: number; phrase: string; kind: 'exact' | 'full' | 'half' | 'rest'; money: MoneyDTO | null; mismatch?: string;
}
export interface Installment { amount: MoneyDTO; promisedOn: string }
/**
 * A promise read from a reply. A question means a person must confirm it; installments are then never filled in.
 * `approximate`: at least one date was read from a rough time ("this week", "first week of December"), so the
 * acknowledgement states the date for the customer to correct.
 */
export interface PromiseSchedule { installments: Installment[]; question: string | null; approximate?: true }

const dollarCurrencies = new Set(['USD','CAD','AUD','NZD','SGD','HKD','MXN']);
const symbolCurrencies: Record<string, string> = { '€':'EUR', '£':'GBP', '¥':'JPY' };
// [code] [symbol] number[.fraction] [k] [code] [dollars]; a bare number is never money.
const exact = new RegExp(String.raw`(?:\b([A-Z]{3})\s?)?([$€£¥])?\s?(?<![\w.,-])(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?`
  + String.raw`(?:\s?([kK])\b)?(?:\s?([A-Z]{3})\b)?(?:\s([Dd]ollars))?`, 'g');
const phrases: [AmountMention['kind'], RegExp][] = [
  ['rest', new RegExp(String.raw`\b(?:the rest|the remainder|(?:the )?remaining (?:balance|amount)|the balance remaining|remainder`
    + String.raw`|what(?:['’]s| is) left)\b`, 'gi')],
  ['full', new RegExp(String.raw`\b(?:in full|(?:the )?(?:full|entire|whole|total) (?:balance|amount|invoice)`
    + String.raw`|(?:the )?(?:outstanding|open) balance|everything(?: we owe)?|all of it|the balance)\b`, 'gi')],
  ['half', /\bhalf\b/gi],
];

function isCurrency(code: string) { try { currencyExponent(code); return true; } catch { return false; } }

/** Amounts in a reply. Bare numbers are never money: an amount needs a currency symbol, code or "dollars". */
export function amountMentions(text: string, currency: string | null): AmountMention[] {
  const found: AmountMention[] = []; const taken: [number, number][] = [];
  const add = (mention: AmountMention, length: number) => {
    if (taken.some(([a, b]) => mention.index < b && mention.index + length > a)) return;
    taken.push([mention.index, mention.index + length]); found.push(mention);
  };
  for (const match of text.matchAll(exact)) {
    const [, before, symbol, whole, fraction, thousands, after, dollars] = match;
    const codes = [before, after].filter((code): code is string => !!code && isCurrency(code));
    if (!codes.length && !symbol && !dollars) continue;
    const named = codes[0] ?? (symbol && symbol !== '$' ? symbolCurrencies[symbol] : undefined)
      ?? (currency && dollarCurrencies.has(currency) ? currency : 'USD');
    const mention: AmountMention = { index:match.index, phrase:match[0].trim(), kind:'exact', money:null };
    if (named !== currency) mention.mismatch = named;
    else {
      try {
        const decimal = `${whole!.replaceAll(',','')}${fraction ? `.${fraction}` : ''}`;
        const parsed = parseMoney(decimal, named);
        const minor = thousands ? parsed.minor * 1000n : parsed.minor;
        if (minor > 0n) mention.money = { minor:minor.toString(), currency:named };
      } catch { /* An amount with more decimals than the currency allows stays unread. */ }
    }
    add(mention, match[0].length);
  }
  for (const [kind, pattern] of phrases) {
    for (const match of text.matchAll(pattern)) add({ index:match.index, phrase:match[0], kind, money:null }, match[0].length);
  }
  return found.sort((a, b) => a.index - b.index);
}

/** Sentences and "and"-joined clauses, so "$2,000 on Oct 9 and the rest end of month" reads as two installments. */
function clauses(text: string) {
  const parts: string[] = []; let start = 0;
  for (const match of text.matchAll(/[.!?;]+(?=\s|$)|\n+|,?\s+(?:and then|then|and|plus)\s+/gi)) {
    const before = text.slice(start, match.index);
    if (match[0].startsWith('.') && monthAbbreviation.test(before)) continue;
    parts.push(before); start = match.index + match[0].length;
  }
  parts.push(text.slice(start));
  return parts.map(part => part.trim()).filter(Boolean);
}

type ScheduleDate = DateMention & { approximate?: true };
/**
 * The upcoming dates of one clause. Exact mentions stand; a rough time ("this week", "in a couple of days") is read as the
 * day `approximateMentions` gives and marked approximate. Two phrases for the same day ("Friday, end of the week") are one.
 */
function upcomingDates(clause: string, receivedOn: string): ScheduleDate[] {
  const rough = approximateMentions(clause, receivedOn); const used = new Set<number>();
  const dates: ScheduleDate[] = [];
  for (const mention of dateMentions(clause, receivedOn)) {
    const end = mention.index + mention.phrase.length;
    const overlap = rough.findIndex(r => mention.index < r.end && end > r.index);
    if (overlap >= 0) used.add(overlap);
    const reading = overlap >= 0 ? rough[overlap]! : null;
    if (mention.past) continue;
    if (reading?.date && !mention.date && !mention.alternatives.length) {
      dates.push({ ...mention, phrase:reading.phrase, index:reading.index, date:reading.date, approximate:true });
    } else if (reading?.date && mention.date === reading.date) dates.push({ ...mention, approximate:true });
    else dates.push(mention);
  }
  rough.forEach((r, index) => {
    if (used.has(index)) return;
    dates.push({ phrase:r.phrase, index:r.index, date:r.date, alternatives:[], past:false, ...(r.date ? { approximate:true as const } : {}) });
  });
  const days = new Set(dates.map(d => d.date));
  if (dates.length > 1 && days.size === 1 && !days.has(null)) {
    // Prefer the exact phrase ("Friday") over the rough one ("end of the week") when both name the same day.
    return [dates.find(d => !d.approximate) ?? dates[0]!];
  }
  return dates.sort((a, b) => a.index - b.index);
}

const ask = (question: string): PromiseSchedule => ({ installments:[], question });
function dateQuestion(mention: DateMention) {
  const readings = mention.alternatives.map(shortDate).join(' or ');
  if (readings) return `“${mention.phrase}” could mean ${readings}. Confirm the date.`;
  return `“${mention.phrase}” is not a specific date. Confirm the date.`;
}

/**
 * Exact installments a reply promises against a known open balance. Every amount is in minor units of the invoice
 * currency; anything unclear (an ambiguous date, a missing amount, a total over the balance) returns a question.
 */
export function promiseSchedule(text: string, balance: Money | null, receivedOn: string): PromiseSchedule {
  const parts: { amount?: AmountMention; date?: ScheduleDate }[] = [];
  for (const clause of clauses(text)) {
    const dates = upcomingDates(clause, receivedOn);
    const amounts = amountMentions(clause, balance?.currency ?? null);
    if (dates.length > 1) return ask(`Several dates were mentioned (${dates.map(d => `“${d.phrase}”`).join(', ')}). Confirm the date.`);
    if (amounts.length > 1) return ask('Several amounts were mentioned in one sentence. Confirm the amount.');
    if (!dates.length && !amounts.length) continue;
    parts.push({ ...(amounts[0] ? { amount:amounts[0] } : {}), ...(dates[0] ? { date:dates[0] } : {}) });
  }
  if (!parts.length) return ask('No date was stated. Confirm when they will pay.');
  for (const part of parts) {
    if (!part.date) return ask(`No date for ${part.amount!.phrase}. Confirm when they will pay.`);
    if (!part.date.date) return ask(dateQuestion(part.date));
    const invoiced = balance?.currency ?? 'another currency';
    if (part.amount?.mismatch) return ask(`Mentions ${part.amount.mismatch} but the invoices are in ${invoiced}. Confirm the amount.`);
    if (part.amount?.kind === 'exact' && !part.amount.money) return ask(`${part.amount.phrase} is not a valid amount. Confirm the amount.`);
  }
  if (!balance || balance.minor <= 0n) return ask('The open balance is unknown, so the amount can’t be checked. Confirm the amount.');
  const total = balance.minor; const currency = balance.currency;
  // What the other installments cover, so "the rest" is the balance after them (half counts as half of it).
  const explicit = parts.reduce((sum, part) => sum + (part.amount?.money ? BigInt(part.amount.money.minor)
    : part.amount?.kind === 'half' ? total / 2n : 0n), 0n);
  const installments: Installment[] = [];
  for (const part of parts) {
    const kind = part.amount?.kind ?? (parts.length === 1 ? 'full' : null);
    if (!kind) return ask(`No amount for “${part.date!.phrase}”. Confirm the amount.`);
    // "The rest will be paid on the 15th" with nothing else promised: the rest of what is unclear (other invoices in the
    // reply may be disputed or already paid), so it is never read as the whole balance.
    if (kind === 'rest' && parts.length === 1) return ask(`“${part.amount!.phrase}” does not say how much. Confirm the amount.`);
    if (kind === 'full' && parts.length > 1) return ask('A full payment and other installments were both mentioned. Confirm the schedule.');
    if (kind === 'half' && total % 2n !== 0n) return ask('Half of the balance is not a whole minor unit. Confirm the amount.');
    const minor = kind === 'exact' ? BigInt(part.amount!.money!.minor)
      : kind === 'full' ? total : kind === 'half' ? total / 2n : total - explicit;
    if (minor <= 0n) return ask('Nothing would remain for the rest of the balance. Confirm the schedule.');
    installments.push({ amount:{ minor:minor.toString(), currency }, promisedOn:part.date!.date! });
  }
  const promised = installments.reduce((sum, item) => sum + BigInt(item.amount.minor), 0n);
  if (parts.filter(part => part.amount?.kind === 'rest').length > 1) return ask('The rest was mentioned twice. Confirm the schedule.');
  if (promised > total) {
    const said = formatMoney({ minor:promised, currency }); const open = formatMoney({ minor:total, currency });
    return ask(`${said} is more than the open balance of ${open}. Confirm the amount.`);
  }
  const dates = new Set(installments.map(item => item.promisedOn));
  if (dates.size !== installments.length) return ask('Two installments share a date. Confirm the schedule.');
  const sorted = installments.sort((a, b) => a.promisedOn.localeCompare(b.promisedOn));
  return { installments:sorted, question:null, ...(parts.some(part => part.date!.approximate) ? { approximate:true as const } : {}) };
}
